import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { toast } from "react-toastify";
import { ProductCategory } from "features/ProductCategories/types/product-category.types";
import {
  deleteProductCategory,
  getProductCategories,
} from "features/ProductCategories/services/ProductCategoryService";
import { getDocumentTitle } from "utils/helpers";
import { ProductCategoryCreateModal } from "../ProductCategoryForm/ProductCategoryCreateModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const ProductCategoryList = () => {
  document.title = getDocumentTitle("Product categories");
  const { can } = useAccess();
  const canUpdateCategory = can(Permissions.PRODUCT.UPDATE);
  const canDeleteCategory = can(Permissions.PRODUCT.DELETE);

  const tableRef = useRef<() => void>(null);
  const [selected, setSelected] = useState<ProductCategory | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const openCreate = useCallback(() => {
    setCategoryModalOpen(true);
  }, []);

  const closeCategoryModal = useCallback(() => {
    setCategoryModalOpen(false);
  }, []);

  const columns: ColumnDef<ProductCategory>[] = useMemo(
    () => [
      IndexColumn<ProductCategory>(),
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.name}</span>
        ),
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const active = row.original.isActive;
          return (
            <span
              className={`badge bg-${active ? "success" : "secondary"}-subtle text-${active ? "success" : "secondary"} rounded-pill`}
            >
              {active ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const hasMenuItems = canUpdateCategory || canDeleteCategory;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Category actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canUpdateCategory ? (
                  <DropdownItem
                    tag={Link}
                    to={`/inventory-management/product-categories/edit/${r.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDeleteCategory ? (
                  <>
                    {canUpdateCategory ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => {
                        setSelected(r);
                        setDeleteOpen(true);
                      }}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canDeleteCategory, canUpdateCategory],
  );

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteProductCategory(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
      setDeleteOpen(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Delete failed",
      );
    }
  };

  return (
    <React.Fragment>
      <ProductCategoryCreateModal
        show={categoryModalOpen}
        editingCategory={null}
        onClose={closeCategoryModal}
        onSuccess={() => tableRef.current?.()}
      />
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={selected?.name}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Product categories"
            pageTitle="Inventory Management"
          />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Categories</h5>
                <p className="text-muted small mb-0">
                  Manage categories used on products. Names must be unique.
                </p>
              </div>
              {can(Permissions.PRODUCT.CREATE) ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreate}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add category
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getProductCategories}
                pageSize={10}
                globalSearch
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                SearchPlaceholder="Search categories by name…"
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductCategoryList;
